import React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Typography,
  useTheme,
} from '@mui/material';
import {
  CheckCircle,
  Error,
  Warning,
  Info,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledDialog = styled(Dialog)(({ theme, alerttype }) => {
  const getBackgroundColor = () => {
    switch (alerttype) {
      case 'success':
        return theme.palette.success.main;
      case 'danger':
      case 'error':
        return theme.palette.error.main;
      case 'warning':
        return theme.palette.warning.main;
      case 'info':
        return theme.palette.info.main;
      default:
        return theme.palette.primary.main;
    }
  };

  return {
    '& .MuiDialog-paper': {
      borderRadius: 16,
      padding: theme.spacing(1),
      minWidth: 400,
      maxWidth: 500,
    },
    '& .MuiDialogTitle-root': {
      backgroundColor: getBackgroundColor(),
      color: theme.palette.common.white,
      padding: theme.spacing(2),
      borderRadius: '16px 16px 0 0',
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    '& .MuiDialogContent-root': {
      padding: theme.spacing(3),
    },
    '& .MuiDialogActions-root': {
      padding: theme.spacing(2),
      justifyContent: 'center',
    },
  };
});

const getIcon = (type) => {
  const iconProps = { sx: { fontSize: 24 } };
  
  switch (type) {
    case 'success':
      return <CheckCircle {...iconProps} />;
    case 'danger':
    case 'error':
      return <Error {...iconProps} />;
    case 'warning':
      return <Warning {...iconProps} />;
    case 'info':
      return <Info {...iconProps} />;
    default:
      return <Info {...iconProps} />;
  }
};

const getTitle = (type) => {
  switch (type) {
    case 'success':
      return 'Success';
    case 'danger':
    case 'error':
      return 'Error';
    case 'warning':
      return 'Warning';
    case 'info':
      return 'Information';
    default:
      return 'Alert';
  }
};

const SimpleAlert = ({
  open,
  onClose,
  text,
  type = 'info',
}) => {
  const theme = useTheme();

  const handleClose = () => {
    onClose();
  };

  const getButtonColor = () => {
    switch (type) {
      case 'success':
        return 'success';
      case 'danger':
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
        return 'info';
      default:
        return 'primary';
    }
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      alerttype={type}
      aria-labelledby="simple-alert-dialog-title"
      aria-describedby="simple-alert-dialog-description"
    >
      <DialogTitle id="simple-alert-dialog-title">
        {getIcon(type)}
        <Typography variant="h6" component="span" sx={{ fontFamily: 'Poppins', fontWeight: 600 }}>
          {getTitle(type)}
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <DialogContentText
          id="simple-alert-dialog-description"
          sx={{
            color: theme.palette.text.primary,
            fontFamily: 'Poppins',
            fontSize: '16px',
            lineHeight: 1.6,
          }}
        >
          {text}
        </DialogContentText>
      </DialogContent>
      
      <DialogActions>
        <Button
          onClick={handleClose}
          variant="contained"
          color={getButtonColor()}
          sx={{
            fontFamily: 'Poppins',
            textTransform: 'none',
            borderRadius: 8,
            px: 4,
            py: 1,
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            },
          }}
        >
          OK
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default SimpleAlert;